import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChildConfigs } from '@app-core/models/core.model';
import { DataService } from '@app-core/services/data/data.service';
import { API } from '@app-core/constants/api.constants';
import { IncidentDetailsParams } from '@app-incidents/models/incidents.models';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { IncidentModalComponent } from '@app-shared/components/incident-modal/incident-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { IncidentModalV2Component } from '@app-shared/components/incident-modal-v2/incident-modal-v2.component';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';

@Injectable({
  providedIn: 'root',
})
export class IncidentsService {
  public modifiedEventList = [];

  constructor(
    private dataService: DataService,
    private http: HttpClient,
    public snackbarService: SnackBarService,
    public dialog: MatDialog,
    public translate: TranslateService,
    private durationService: DurationRangeService
  ) {
    const combinedEventsList = this.dataService.modifyFleeEvents();
    this.modifiedEventList = combinedEventsList.map((k) => ({
      key: k['key'],
      label: k['label'],
      childConfigs: k['childConfigs'],
      isCollisionEvent: k.isCollisionEvent,
    }));
  }

  private setParams(params: any) {
    return new HttpParams({
      fromObject: params,
    });
  }

  public getChildConfigs(eventKey: string): ChildConfigs[] {
    if (!this.modifiedEventList.length) {
      const combinedEventsList = this.dataService.modifyFleeEvents();
      this.modifiedEventList = combinedEventsList.map((k) => ({
        key: k['key'],
        label: k['label'],
        childConfigs: k['childConfigs'],
        isCollisionEvent: k.isCollisionEvent,
      }));
    }
    const selectedEvent = this.modifiedEventList.find((event) => event.key === eventKey);
    if (selectedEvent && selectedEvent.childConfigs) {
      return Object.entries(selectedEvent.childConfigs).map(([key, value]: [string, { label: string }]) => ({ key, label: value.label }));
    }
    return [];
  }

  public getDefaultSubEventType(eventType: string) {
    const childConfigs = this.getChildConfigs(eventType);
    if (childConfigs.length) {
      return childConfigs[0].key;
    }
  }

  public getIncidentDetails(params: IncidentDetailsParams) {
    const options = {
      params: this.setParams(params),
    };
    return this.http.get(API.EVENT_DETAILS, options);
  }

  public getIncidentDetailsAndOpenDialog(options: IncidentDetailsParams) {
    return this.getIncidentDetails(options).pipe(
      tap((eventDetails) => {
        this.showMedia(eventDetails);
      }),
      catchError(() => {
        this.snackbarService.failure(this.translate.instant('notificationConfiguration.Unable to find the event details at this moment'));
        return of(null);
      })
    );
  }

  public showMedia(eventDetails: any) {
    if (this.durationService.enableNewIncidentModal) {
      this.dialog.open(IncidentModalV2Component, {
        panelClass: ['incident-modal-v2', 'mobile-modal'],
        position: { top: '24px', bottom: '24px' },
        autoFocus: false,
        disableClose: true,
        data: {
          source: 'IncidentView',
          allEvents: [eventDetails],
          currentIndex: 0,
          hidePagination: true,
        },
      });
    } else {
      this.dialog.open(IncidentModalComponent, {
        panelClass: ['incident-modal', 'mobile-modal'],
        position: { top: '24px', bottom: '24px' },
        autoFocus: false,
        disableClose: true,
        data: {
          source: 'IncidentView',
          allEvents: [eventDetails],
          currentIndex: 0,
          hidePagination: true,
        },
      });
    }
  }
}
